import { AxiosResponse } from 'axios';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';

import { ClaimsColumn } from './ClaimsColumn';
import { ConfirmationModal } from './ConfirmationModal';
import ProductPrice from './ProductPrice';
import { ReplacementOrderModal } from './ReplacementOrderModal';
import { ReturnsColumn } from './ReturnsColumn';
import { FulfillmentsTable } from './Table';
import { InfoTooltip } from './Tooltip';
import { axios, getReturnInfo, syncFulfillmentWithAftership } from '../api';
import { Pencil } from '../assets/Pencil';
import { RefreshIcon } from '../assets/RefreshIcon';
import { InlineSpinner } from '../assets/Spinner';
import { TRACKING_URL } from '../config';
import { SEVEN_DAYS_MS } from '../constants';
import { isOpsAdmin, isAllModuleAccessUser, isAdmin } from '../pages/constants';
import {
  ClaimTypes,
  Customer,
  Fulfillment,
  Order,
  Pet,
  ProductReturn,
  Shipment,
  FulfillmentStatus,
  Item,
  OrderFulfillmentProduct,
} from '../pages/types';
import { FulfillmentHeader } from '../tableHeaders';
import {
  BOTTOM_TOAST,
  calculatePriceAfterDiscountWithTax,
  isProductReturnVisible,
  mapFulfillments,
  getPetNameForFulfillmentProduct,
  getShipmentTooltipFields,
} from '../utils';

interface OrderFulfillmentsProps {
  customer: Customer;
  order: Order;
  refreshCustomer: () => void;
}

const formatMatchingClaimTypes = (claimTypes: string[]) => {
  if (!claimTypes || claimTypes.length === 0) return;
  return claimTypes
    .map((type) => {
      return ClaimTypes[type as keyof typeof ClaimTypes];
    })
    .join(', ');
};

const getPetNamesFromFulfillments = (petIds: string[], allPets: Pet[]) => {
  if (!petIds) {
    return '--';
  }
  const petNames = new Set();
  petIds.forEach((pet_id: string) => {
    const name = allPets.find((pet: Pet) => pet.id === pet_id)?.name;
    if (name) petNames.add(name);
  });

  const petNamesConcat = Array.from(petNames).join(', ');
  return petNamesConcat !== '' ? petNamesConcat : '--';
};

const getShipmentTooltip = (shipment: Shipment | undefined) => {
  if (!shipment) return 'No tracking information';

  const { mostRecentTimestamp, mostRecentStatus } = getShipmentTooltipFields(shipment);

  if (!mostRecentStatus) {
    return 'No tracking information';
  }

  return (
    <div className="text-left">
      Last update from AfterShip <br /> {mostRecentStatus} on{' '}
      {mostRecentTimestamp?.toLocaleString()}
    </div>
  );
};

export const useSplitFulfillment = () => {
  const navigate = useNavigate();

  return (fulfillmentId: number) => {
    const curr = window.location.pathname;
    const fulfillmentPath = `${curr}/${fulfillmentId}/split-fulfillment`;
    navigate(fulfillmentPath);
  };
};

interface ClaimData {
  claim_id: string;
  pet_id: string;
  fulfillment_id: string;
  product_id: string;
  claim_types: string[];
}

export const OrderFulfillments = ({ customer, order, refreshCustomer }: OrderFulfillmentsProps) => {
  const [selectedFulfilmentId, setSelectedFulfilmentId] = useState<string | null>(null);
  const [selectedFulfillmentIdForSync, setSelectedFulfillmentIdForSync] = useState<string | null>(
    null
  );
  const [isProcessLoading, setIsProcessLoading] = useState(false);
  const [selectedClaimId, setSelectedClaimId] = useState<string | null>(null);
  const [isDeleteClaimLoading, setIsDeleteClaimLoading] = useState(false);
  const [claims, setClaims] = useState<ClaimData[]>([]);
  const [isFetchingClaims, setIsFetchingClaims] = useState(false);
  const [returns, setReturns] = useState<ProductReturn[]>([]);
  const [isFetchingReturns, setIsFetchingReturns] = useState(false);

  const UserHasDeleteAccess = !!(isAdmin() || isOpsAdmin() || isAllModuleAccessUser());

  useEffect(() => {
    const fetchClaimDetails = async () => {
      setIsFetchingClaims(true);
      try {
        const response = await axios.get(`/claims/order/${order.id}`);
        setClaims(response.data);
      } catch (error) {
        toast.error('Failed to fetch claim details', BOTTOM_TOAST);
        console.error('Error fetching claim details:', error);
      } finally {
        setIsFetchingClaims(false);
      }
    };

    if (!isDeleteClaimLoading) {
      fetchClaimDetails();
    }
  }, [order.id, isDeleteClaimLoading]);

  useEffect(() => {
    const fetchReturnDetails = async () => {
      setIsFetchingReturns(true);
      try {
        const returnInfo = await getReturnInfo({ orderId: order.id });
        setReturns(returnInfo.data);
      } catch (error) {
        console.warn(`Error fetching return details for order ${order.id}: ${error}`);
      } finally {
        setIsFetchingReturns(false);
      }
    };

    fetchReturnDetails();
  }, [order.id]);

  const getTrackingNumber = (
    product: OrderFulfillmentProduct,
    fulfillmentHasMealProduct: boolean,
    isFirstFulfillmentProduct: boolean
  ) => {
    const hasShipments = product.fulfillment.shipments.length > 0;
    const fulfillmentIsShipped = product.fulfillment.status === FulfillmentStatus.SHIPPED;
    const isSplitOrMeal = product.fulfillment.is_split_fulfillment || product.isMealProduct;
    const isFirstOnNonMeal = !fulfillmentHasMealProduct && isFirstFulfillmentProduct;
    if (hasShipments && (isSplitOrMeal || isFirstOnNonMeal)) {
      return (
        <ul className="flex flex-col items-center justify-center w-full">
          {product.fulfillment.shipments.map((shipment: Shipment) => (
            <li
              key={shipment.tracking_number}
              className={`flex ${
                fulfillmentIsShipped ? 'flex-col' : ''
              } items-center justify-center gap-2`}
            >
              <a
                onClick={() => window.open(`${TRACKING_URL}/${shipment.tracking_number}`)}
                className="cursor-pointer underline text-anchor text-center"
              >
                {shipment.tracking_number}
              </a>
              <div className={`flex flex-row gap-2 ${fulfillmentIsShipped ? 'mr-2' : ''}`}>
                {fulfillmentIsShipped && (
                  <RefreshIcon
                    className="inline w-10 h-6 mx-1 cursor-pointer"
                    onClick={() => setSelectedFulfillmentIdForSync(product.fulfillment.id)}
                  />
                )}
                <InfoTooltip text={getShipmentTooltip(shipment)} />
              </div>
            </li>
          ))}
        </ul>
      );
    }
    return '--';
  };

  const handleDeleteClaim = () => {
    setIsDeleteClaimLoading(true);
    axios
      .delete(`/claims/${selectedClaimId}`)
      .then(() => {
        refreshCustomer();
        setIsDeleteClaimLoading(false);
        setSelectedClaimId(null);
        toast.success('Claim deleted successfully', BOTTOM_TOAST);
      })
      .catch(() => {
        setIsDeleteClaimLoading(false);
        setSelectedClaimId(null);
        toast.error('Failed to delete claim', BOTTOM_TOAST);
      });
  };

  const canReplaceOrder =
    order.order_type !== 'REPLACEMENT' &&
    ['PARTIALLY_FULFILLED', 'FULFILLED', 'COMPLETE'].some((status) =>
      order.status.includes(status)
    );
  const [showReplacementOrderModal, setShowReplacementOrderModal] = useState(false);
  const mappedFulfillmentProducts = mapFulfillments(order);
  const fulfillmentHasMealProduct = mappedFulfillmentProducts.some(
    (product: OrderFulfillmentProduct) => product.isMealProduct
  );

  const handleUpdateFulfillment = () => {
    setIsProcessLoading(true);
    axios
      .put(`/orders/mark_fulfillment_delivery_failed/`, { fulfillment_id: selectedFulfilmentId })
      .then(() => {
        refreshCustomer();
        setIsProcessLoading(false);
        setSelectedFulfilmentId(null);
        toast.success('Fulfillment status updated successfully', BOTTOM_TOAST);
      })
      .catch(() => {
        setIsProcessLoading(false);
        setSelectedFulfilmentId(null);
        toast.error('Failed to update fulfillment status', BOTTOM_TOAST);
      });
  };

  const getConfirmationTitle = () => {
    const fulfillment = order.fulfillments.find((fulfillment: Fulfillment) => {
      return fulfillment.id === selectedFulfilmentId;
    });
    return (
      <p>
        Mark {getPetNamesFromFulfillments(fulfillment?.pet_ids ?? [], customer.pets)}'s fulfillment
        as <span className="font-bold">“Delivery Failed”?</span>
      </p>
    );
  };

  const getConfirmationMessage = () => {
    return (
      <p>
        This action will update the fulfillment’s status from{' '}
        <span className="font-bold">“Shipped”</span> to{' '}
        <span className="font-bold">“Delivery Failed.”</span>
      </p>
    );
  };

  const isFulfillmentSplittable = (fulfillment: Fulfillment) => {
    return (
      !fulfillment.is_split_fulfillment &&
      fulfillment.status === FulfillmentStatus.ORDERDESK_SENT &&
      fulfillment.products.length > 1
    );
  };

  const getFulfillmentStatus = (
    product: OrderFulfillmentProduct,
    fulfillmentHasMealProduct: boolean,
    isFirstFulfillmentProduct: boolean
  ) => {
    const isSplitOrMeal = product.fulfillment.is_split_fulfillment || product.isMealProduct;
    const isFirstOnNonMeal = !fulfillmentHasMealProduct && isFirstFulfillmentProduct;
    if (isSplitOrMeal || isFirstOnNonMeal) {
      return (
        <div className="flex items-center justify-center space-x-1 w-full">
          <span>{product.fulfillment?.status ? product.fulfillment?.status : ''}</span>
          {product.fulfillment?.status === 'SHIPPED' && (
            <a
              onClick={() => setSelectedFulfilmentId(product.fulfillment.id)}
              className="flex items-center justify-center cursor-pointer underline text-anchor"
            >
              <Pencil className="w-4 h-4 text-black" />
            </a>
          )}
        </div>
      );
    }
    return '--';
  };

  const isShipmentStuck = (shipment: any) => {
    const updatedAt = new Date(shipment.updated_at);
    const now = new Date();
    return (
      shipment.current_status === 'InTransit' &&
      shipment.updated === false &&
      now.getTime() - updatedAt.getTime() > SEVEN_DAYS_MS
    );
  };

  const getSyncShipmentStatusMessage = () => {
    return (
      <p>
        This action will sync the fulfillment status with AfterShip for{' '}
        <span className="font-bold">all shipments</span> on fulfillment ID{' '}
        {selectedFulfillmentIdForSync}.
      </p>
    );
  };

  const handleSyncShipmentStatus = () => {
    if (selectedFulfillmentIdForSync === null) return;
    syncFulfillmentWithAftership({ fulfillmentId: selectedFulfillmentIdForSync })
      .then((payload: AxiosResponse<any, any>) => {
        setIsProcessLoading(false);
        setSelectedFulfillmentIdForSync(null);
        let syncProblem = '';
        payload.data.shipments_updated?.forEach((shipment: any) => {
          if (isShipmentStuck(shipment)) {
            syncProblem +=
              '\n' +
              `Note: Shipment ${shipment.tracking_number} has been in SHIPPED status for over 7 days`;
          }
          if (shipment.return_to_sender) {
            syncProblem +=
              '\n' +
              `Note: Shipment ${shipment.tracking_number} was returned to sender and marked as Delivery Failed`;
          }
        });
        toast.success(
          <div>
            Fulfillment status synced successfully
            {syncProblem && <div className="text-sm">{syncProblem}</div>}
          </div>,
          BOTTOM_TOAST
        );
      })
      .catch(() => {
        setIsProcessLoading(false);
        setSelectedFulfillmentIdForSync(null);
        toast.error('Failed to update fulfillment status', BOTTOM_TOAST);
      });
  };

  const parseProductItems = (product: OrderFulfillmentProduct) => {
    if (!product || !Array.isArray(product.items) || product.items.length === 0) {
      return '--';
    }

    const itemStrings: string[] = [];

    product.items.forEach((item: Item) => {
      itemStrings.push(`${item.quantity} X ${item.sku}`);
    });

    return itemStrings;
  };

  const findMatchingReturn = (
    productToMatch: OrderFulfillmentProduct
  ): ProductReturn | undefined => {
    return returns.find(
      (productReturn) =>
        JSON.parse(productReturn.items).some(
          (item: { id: string }) =>
            item.id.includes(productToMatch.code) || productToMatch.code.includes(item.id)
        ) && productReturn.pet_ids?.includes(productToMatch.pet_id)
    );
  };

  const renderProductReturns = (
    product: OrderFulfillmentProduct,
    returnableProducts: OrderFulfillmentProduct[],
    matchingReturn?: ProductReturn
  ) => {
    if (isFetchingReturns)
      return (
        <div className="flex justify-center">
          <InlineSpinner />
        </div>
      );

    if (isProductReturnVisible(product))
      return (
        <ReturnsColumn
          productReturn={matchingReturn}
          customer={customer}
          order={order}
          products={returnableProducts}
        />
      );

    return '--';
  };

  const seenFulfillmentIds = new Set<string>();

  return (
    <div className="pt-3 pb-6">
      <h2 className="mb-1 flex items-center">
        <b className="inline-block">Fulfillments</b>
        {canReplaceOrder && (
          <div className="text-sm ml-2">
            <a
              onClick={() => setShowReplacementOrderModal(true)}
              className="cursor-pointer underline text-anchor"
            >
              Replace Order
            </a>
          </div>
        )}
      </h2>
      {showReplacementOrderModal && (
        <ReplacementOrderModal
          customer={customer}
          order={order}
          onConfirm={() => setShowReplacementOrderModal(false)}
          onCancel={() => setShowReplacementOrderModal(false)}
        />
      )}
      <div className="bg-white">
        <FulfillmentsTable
          currency={customer.default_currency}
          header={FulfillmentHeader}
          data={mappedFulfillmentProducts.map((product: OrderFulfillmentProduct, index: number) => {
            // Find matching claim for this product and fulfillment
            const matchingClaim = claims.find(
              (claim) =>
                claim.fulfillment_id === product.fulfillment.id &&
                claim.product_id === product.id &&
                claim.pet_id === product.pet_id
            );

            const matchingReturn = findMatchingReturn(product);
            const isFirstFulfillmentProduct = index === 0;
            let fulfillmentId = product.fulfillment.id;
            if (seenFulfillmentIds.has(product.fulfillment.id)) {
              fulfillmentId = '--';
            } else {
              seenFulfillmentIds.add(product.fulfillment.id);
            }

            // Controls what products are available to return in the modal
            // Products with a return in progress should not be allowed here.
            const returnableProducts = mappedFulfillmentProducts.filter(
              (displayProduct) =>
                isProductReturnVisible(displayProduct) && !findMatchingReturn(displayProduct)
            );

            return {
              'Fulfillment ID': fulfillmentId,
              'Fulfillment Center':
                fulfillmentId !== '--' ? product.fulfillment.fulfillment_center : '--',
              Is_Meal_Product: product.isMealProduct,
              Is_Splittable: isFulfillmentSplittable(product.fulfillment),
              'Fulfillment Status': getFulfillmentStatus(
                product,
                fulfillmentHasMealProduct,
                isFirstFulfillmentProduct
              ),
              'Tracking #s': getTrackingNumber(
                product,
                fulfillmentHasMealProduct,
                isFirstFulfillmentProduct
              ),
              Pet: getPetNameForFulfillmentProduct(product, customer.pets),
              'Product Code': (
                <>
                  {product.code}
                  {matchingClaim && matchingClaim.claim_types && (
                    <>
                      <Tooltip id="claim-tooltip" place="top" />
                      <span
                        className="ml-1 text-red-600"
                        title="There is a claim associated with this product"
                        data-tooltip-id="claim-tooltip"
                        data-tooltip-content={`Claim Type(s) [${formatMatchingClaimTypes(
                          matchingClaim.claim_types
                        )}] were submitted on this product.`}
                      >
                        ❗
                      </span>
                    </>
                  )}
                </>
              ),
              'Product Description': product.description ?? '--',
              'Total Items': `${parseProductItems(product)}`,
              Price: <ProductPrice product={product} />,
              'Price with Tax': `$${calculatePriceAfterDiscountWithTax(product)}`,
              Claims: isFetchingClaims ? (
                <div className="flex justify-center">
                  <InlineSpinner />
                </div>
              ) : (
                <ClaimsColumn
                  claimId={matchingClaim?.claim_id || null}
                  onDeleteClick={setSelectedClaimId}
                  userHasDeleteAccess={UserHasDeleteAccess}
                  product={product}
                  customer={customer}
                />
              ),
              'Product Returns': renderProductReturns(product, returnableProducts, matchingReturn),
            };
          })}
        />
      </div>
      {selectedFulfilmentId && (
        <ConfirmationModal
          isProcessLoading={isProcessLoading}
          title={getConfirmationTitle()}
          message={getConfirmationMessage()}
          confirmLabel="Confirm"
          onConfirm={handleUpdateFulfillment}
          onCancel={() => setSelectedFulfilmentId(null)}
        />
      )}
      {selectedClaimId && (
        <ConfirmationModal
          isProcessLoading={isDeleteClaimLoading}
          title="Are you sure you want to delete this claim?"
          message="Please confirm before proceeding, as claims cannot be restored once deleted."
          confirmLabel="Confirm"
          onConfirm={handleDeleteClaim}
          onCancel={() => setSelectedClaimId(null)}
        />
      )}
      {selectedFulfillmentIdForSync && (
        <ConfirmationModal
          title="Sync Fulfillment Status with AfterShip?"
          message={getSyncShipmentStatusMessage()}
          confirmLabel="Confirm"
          onConfirm={handleSyncShipmentStatus}
          onCancel={() => setSelectedFulfillmentIdForSync(null)}
        />
      )}
    </div>
  );
};
